import type { ITranslations } from '@inter-ikea-kompis/types';

import type ITranslationsResponse from './types/ITranslationsResponse.js';

/**
 * This class is used for transforming translations data.
 */
export default class TranslationsTransformer<T extends ITranslations> {
	private readonly translationPrefixApplication: string | null;

	/**
	 * Constructor.
	 *
	 * @param options Options.
	 * @param options.translationPrefixApplication Translation prefix application.
	 */
	constructor(options?: { translationPrefixApplication?: string }) {
		this.translationPrefixApplication = options?.translationPrefixApplication || null;
	}

	/**
	 * Transforms data into a ITranslations model.
	 *
	 * @param data Response data to parse.
	 * @returns Localized information model.
	 */
	public transform(data: ITranslationsResponse): T {
		return <T>this.replaceApplicationPrefixInKeys(data);
	}

	/**
	 * Recursively replaces application prefix in all keys in the object.
	 *
	 * @param objectOrString Object or string.
	 */
	private replaceApplicationPrefixInKeys(objectOrString: object | string): object | string {
		if (!objectOrString || typeof objectOrString === 'string') {
			return objectOrString;
		}

		const objectWithUpdatedKeys = {};
		for (const key in objectOrString) {
			const updatedKey = this.replaceApplicationPrefixInKey(key);
			if (updatedKey) {
				objectWithUpdatedKeys[updatedKey] = this.replaceApplicationPrefixInKeys(
					objectOrString[key]
				);
			}
		}

		return objectWithUpdatedKeys;
	}

	/**
	 * @param key The key to replace application prefix in.
	 */
	private replaceApplicationPrefixInKey(key: string): string | null {
		const application = this.translationPrefixApplication;
		if (!application) {
			return key;
		}

		const pairs = key.split('__');
		if (pairs.length > 1 && pairs[0] === application) {
			return pairs[1];
		}
		// We should remove other application prefixed keys that don't match the application.
		if (pairs.length > 1) {
			return null;
		}

		return key;
	}
}
